import {
  Box,
  Heading,
  Text,
  Stack,
  createIcon,
  VStack,
  Spinner,
  Fade,
  Button,
  List,
  ListItem,
  Flex,
  Badge,
  Progress,
  Icon
} from "@chakra-ui/react";
import GeneralLayout from "../layout/GeneralLayout";
import { CustomButton } from "../components/Buttons";
import Cookies from "js-cookie";
import { useNavigate, useLocation } from "react-router-dom";
import { USER_LEVELS } from "../util/data";
import { useState, useEffect } from "react";
import { CheckCircleIcon, LockIcon, ArrowForwardIcon, TimeIcon } from "@chakra-ui/icons";
import { color } from "framer-motion";

export default function Welcome() {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const [isDataReady, setIsDataReady] = useState(false);
  const [userLevel, setUserLevel] = useState(null);
  const [showWelcome, setShowWelcome] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  const isLoggedin =
    sessionStorage.getItem("isLoggedin") === "true" ||
    Cookies.get("isLoggedin") === "true";

  // Application levels configuration
  const applicationLevels = [
    {
      level: USER_LEVELS.APPLICATION_PROCESS,
      name: "Application Process",
      description: "Complete your personal information and documents",
      path: "/application/personal-info",
      routes: [
        { name: "Personal Information", path: "/application/personal-info" },
        { name: "Work History", path: "/application/work-history" },
        { name: "Certificates", path: "/application/training/certificate" },
        { name: "Resume Upload", path: "/application/resume" },
        { name: "Address Details", path: "/application/address" },
        { name: "ID Upload", path: "/application/id-upload" },
        { name: "Next of Kin", path: "/application/next-of-kin" },
        { name: "Mobility Status", path: "/application/mobility-status" },
        { name: "Right to Work", path: "/application/right-to-work" }
      ]
    },
    {
      level: USER_LEVELS.INTERVIEW,
      name: "Interview Stage",
      description: "Schedule and complete your interview",
      path: "/interview/schedule",
      routes: [
        { name: "Schedule Interview", path: "/interview/schedule" },
        { name: "Interview Feedback", path: "/interview/feedback" }
      ]
    },
    {
      level: USER_LEVELS.PRE_EMPLOYMENT_CHECK,
      name: "Pre-Employment Check",
      description: "Complete background checks and references",
      path: "/employment-check/provisional-offer",
      routes: [
        { name: "Provisional Offer", path: "/employment-check/provisional-offer" },
        { name: "Right to Work Proof", path: "/employment-check/rtw-proof" },
        { name: "DBS Check", path: "/employment-check/dbs" },
        { name: "Reference Check", path: "/employment-check/referee" }
      ]
    },
    {
      level: USER_LEVELS.CONTRACT_AGREEMENT,
      name: "Onboarding",
      description: "Complete final onboarding steps",
      path: "/onboarding/checklist",
      routes: [
        { name: "Starter Checklist", path: "/onboarding/checklist" },
        { name: "Passport Photo", path: "/onboarding/upload-passport" },
        { name: "Uniform Details", path: "/onboarding/uniform" },
        { name: "Bank Details", path: "/onboarding/bank" },
        { name: "Night Work Agreement", path: "/onboarding/nightwork" },
        { name: "Opt-out Agreement", path: "/onboarding/optout-agreement" },
        { name: "Contract Agreement", path: "/contract-agreement" }
      ]
    },
    {
      level: USER_LEVELS.STAFF,
      name: "Staff Portal",
      description: "Access your staff dashboard and tools",
      path: "/dashboard",
      routes: [
        { name: "Dashboard", path: "/dashboard" },
        { name: "Compliance", path: "/dashboard/compliance" },
        { name: "Timesheet", path: "/dashboard/timesheet" },
        { name: "Profile", path: "/dashboard/profile/edit" }
      ]
    },
    
  ];

  // Enhanced data loading with retry mechanism
  useEffect(() => {
    const loadUserData = async (isRetry = false) => {
      if (!isLoggedin) {
        setIsLoading(false);
        setShowWelcome(true);
        return;
      }

      setIsLoading(true);

      try {
        const storedUserLevel =
          localStorage.getItem("currentUserLevel") ||
          sessionStorage.getItem("userLevel") ||
          Cookies.get("userLevel");

        const hasSessionAuth =
          sessionStorage.getItem("token") &&
          sessionStorage.getItem("uid") &&
          sessionStorage.getItem("isLoggedin") === "true";

        const hasCookieAuth =
          Cookies.get("token") &&
          Cookies.get("uid") &&
          Cookies.get("isLoggedin") === "true";

        const hasAuthData = hasSessionAuth || hasCookieAuth;

        if (storedUserLevel && hasAuthData) {
          const level = parseInt(storedUserLevel, 10);
          setUserLevel(level);
          setIsDataReady(true);
          setIsLoading(false);

          if (!isRetry) {
            setTimeout(() => setShowWelcome(true), 300);
          }
        } else {
          if (retryCount < 3) {
            setTimeout(() => {
              setRetryCount(prev => prev + 1);
              loadUserData(true);
            }, 500);
          } else {
            setIsLoading(false);
            if (!isRetry) {
              setShowWelcome(true);
            }
          }
        }
      } catch (error) {
        console.error('Error loading user data:', error);
        setIsLoading(false);
        setShowWelcome(true);
      }
    };

    loadUserData();
  }, [isLoggedin, retryCount]);

  // Get current level index
  const getCurrentLevelIndex = () => {
    if (!userLevel) return -1;
    return applicationLevels.findIndex(level => level.level === userLevel);
  };

  // Calculate progress percentage
  const getProgressPercentage = () => {
    const currentIndex = getCurrentLevelIndex();
    if (currentIndex === -1) return 0;
    return Math.round((currentIndex / (applicationLevels.length - 1)) * 100);
  };

  // Get level status
  const getLevelStatus = (levelIndex) => {
    const currentIndex = getCurrentLevelIndex();

    if (currentIndex === -1) return "upcoming";
    if (levelIndex < currentIndex) return "completed";
    if (levelIndex === currentIndex) return "current";
    return "upcoming";
  };

  // Handle level click
  const handleLevelClick = (level) => {
    if (level.level <= userLevel) {
      navigate(level.path);
    }
  };

  // Handle route click
  const handleRouteClick = (routePath) => {
    navigate(routePath);
  };

  // Handle manual data refresh
  const handleLoadData = async () => {
    setIsLoading(true);
    setRetryCount(0);

    try {
      const token = Cookies.get("token") || sessionStorage.getItem("token");
      const uid = Cookies.get("uid") || sessionStorage.getItem("uid");
      const userLevel = Cookies.get("userLevel") || sessionStorage.getItem("userLevel");

      if (token && uid) {
        sessionStorage.setItem("token", token);
        sessionStorage.setItem("uid", uid);
        sessionStorage.setItem("isLoggedin", "true");

        if (userLevel) {
          sessionStorage.setItem("userLevel", userLevel);
          localStorage.setItem("currentUserLevel", userLevel);
          const level = parseInt(userLevel, 10);
          setUserLevel(level);
          setIsDataReady(true);
        }

        Cookies.set("isLoggedin", "true", { expires: 1 });
      }

      await new Promise(resolve => setTimeout(resolve, 800));

      const storedUserLevel =
        localStorage.getItem("currentUserLevel") ||
        sessionStorage.getItem("userLevel") ||
        Cookies.get("userLevel");

      const hasAuthData =
        sessionStorage.getItem("token") &&
        sessionStorage.getItem("uid") &&
        sessionStorage.getItem("isLoggedin") === "true";

      if (storedUserLevel && hasAuthData) {
        setUserLevel(parseInt(storedUserLevel, 10));
        setIsDataReady(true);
      }
    } catch (error) {
      console.error('Error refreshing data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Force reload page as last resort
  const handleForceReload = () => {
    window.location.reload();
  };

  const currentLevelIndex = getCurrentLevelIndex();
  const progressPercentage = getProgressPercentage();

  return (
    <GeneralLayout
      pageContent={
        <Box
          display="flex"
          w={{ base: "93%", md: "85%" }}
          m={"auto"}
          my={20}
          py={50}
          gap={2}
          flexDirection={{ base: "column", md: "row" }}
        >
          <Box flex={2}  rounded={20} p={8}>
            <Stack
              maxW={"6xl"}
              m={"auto"}
              as={Box}
              textAlign={"center"}
              spacing={{ base: 8, md: 14 }}
              justifyContent={"center"}
            >
              {!isLoggedin && <Fade in={showWelcome} delay={0.1}>
                <Heading
                  fontWeight={600}
                  fontSize={{ base: "3xl", sm: "5xl", md: "6xl" }}
                  lineHeight={"110%"}
                >
                  Welcome to <br />
                  <Text as={"span"} color={"blue.400"}>
                    Litmus Application Portal
                  </Text>
                </Heading>
              </Fade>}

              {isLoggedin ? (
                <Fade in={showWelcome} delay={0.3}>
                  <VStack spacing={8} width="100%">
                    {/* Loading State */}
                    {isLoading && (
                      <VStack spacing={4} width="100%">
                        <Spinner size="xl" color="blue.400" thickness="4px" />
                        <Text color="gray.600" fontSize="lg">
                          {retryCount > 0 ? `Loading your data... (${retryCount}/3)` : "Loading your application data..."}
                        </Text>
                      </VStack>
                    )}

                    {/* Data Not Ready State */}
                    {!isLoading && !isDataReady && (
                      <VStack spacing={6} width="100%">
                        <VStack spacing={3}>
                          <Text color="gray.600" fontSize="xl" fontWeight="medium">
                            Welcome Back!
                          </Text>
                          <Text color="gray.500" fontSize="md" textAlign="center">
                            We need to load your latest application progress.
                            This ensures you see your most recent information.
                          </Text>
                        </VStack>

                        <VStack spacing={3}>
                          <CustomButton
                            onClick={handleLoadData}
                            label="Load My Application Data"
                            bg={"blue.400"}
                            color={"white"}
                            width={{ base: "350px", md: "300px" }}
                            _hover={{
                              bg: "blue.500",
                              transform: "translateY(-2px)",
                              boxShadow: "lg"
                            }}
                          />

                          <Button
                            variant="link"
                            color="gray.500"
                            size="sm"
                            onClick={handleForceReload}
                          >
                            Still having issues? Click here to reload page
                          </Button>
                        </VStack>
                      </VStack>
                    )}

                    {/* Data Ready State */}
                    {!isLoading && isDataReady && userLevel && (
                      <VStack spacing={8} width="100%" align="stretch">
                        {/* Progress Section */}
                        <Box bg="white" p={6} rounded="lg" shadow="sm">
                          <Flex justify="space-between" align="center" mb={4}>
                            <Text fontSize="lg" fontWeight="semibold">
                              Application Progress
                            </Text>
                            <Badge colorScheme="blue" fontSize="md">
                              {progressPercentage}% Complete
                            </Badge>
                          </Flex>
                          <Progress
                            value={progressPercentage}
                            colorScheme="blue"
                            size="lg"
                            rounded="full"
                            mb={4}
                          />
                          <Text color="gray.600" fontSize="sm">
                            You are currently at: <strong>{applicationLevels[currentLevelIndex]?.name}</strong>
                          </Text>
                        </Box>

                        {/* Application Levels */}
                        <VStack spacing={4} align="stretch">
                          <Text fontSize="xl" fontWeight="semibold" textAlign="left">
                            Application Stages
                          </Text>

                          {applicationLevels.map((level, index) => {
                            const status = getLevelStatus(index);
                            const isCompleted = status === "completed";
                            const isCurrent = status === "current";
                            const isUpcoming = status === "upcoming";

                            return (
                              <Box
                                key={level.level}
                                border="2px"
                                borderColor={
                                  isCurrent ? "blue.400" :
                                    isCompleted ? "green.200" : "gray.200"
                                }
                                bg={
                                  isCurrent ? "blue.50" :
                                    isCompleted ? "green.50" : "white"
                                }
                                p={4}
                                rounded="lg"
                                cursor={isCompleted || isCurrent ? "pointer" : "not-allowed"}
                                onClick={() => handleLevelClick(level)}
                                transition="all 0.2s"
                                _hover={
                                  (isCompleted || isCurrent) ? {
                                    transform: "translateY(-2px)",
                                    shadow: "md"
                                  } : {}
                                }
                              >
                                <Flex align="center" justify="space-between">
                                  <Flex align="center" gap={3}>
                                    <Box>
                                      {isCompleted && (
                                        <Icon as={CheckCircleIcon} color="green.500" boxSize={6} />
                                      )}
                                      {isCurrent && (
                                        <Icon as={TimeIcon} color="blue.500" boxSize={6} />
                                      )}
                                      {isUpcoming && (
                                        <Icon as={LockIcon} color="gray.400" boxSize={6} />
                                      )}
                                    </Box>
                                    <Box textAlign="left">
                                      <Text fontWeight="semibold" color={isUpcoming ? "gray.500" : "gray.800"}>
                                        {level.name}
                                      </Text>
                                      <Text fontSize="sm" color="gray.600">
                                        {level.description}
                                      </Text>
                                    </Box>
                                  </Flex>
                                  <Flex align="center" gap={2}>
                                    {isCurrent && (
                                      <Badge colorScheme="blue" variant="solid">
                                        Current
                                      </Badge>
                                    )}
                                    {isCompleted && (
                                      <Badge colorScheme="green" variant="subtle">
                                        Completed
                                      </Badge>
                                    )}
                                    {(isCompleted || isCurrent) && (
                                      <ArrowForwardIcon color="gray.500" />
                                    )}
                                  </Flex>
                                </Flex>

                                {/* Show routes for current level */}
                                {isCurrent && level.routes.length > 0 && (
                                  <Box mt={4} pl={9}>
                                    <Text fontSize="sm" fontWeight="medium" color="gray.700" mb={2}>
                                      Available steps:
                                    </Text>
                                    <List spacing={2}>
                                      {level.routes.map((route, routeIndex) => (
                                        <ListItem key={routeIndex}>
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleRouteClick(route.path);
                                            }}
                                            leftIcon={<ArrowForwardIcon />}
                                            colorScheme="blue"
                                            justifyContent="flex-start"
                                            width="100%"
                                          >
                                            {route.name}
                                          </Button>
                                        </ListItem>
                                      ))}
                                    </List>
                                  </Box>
                                )}
                              </Box>
                            );
                          })}
                        </VStack>

                        {/* Quick Action Button */}
                        <CustomButton
                          onClick={() => handleLevelClick(applicationLevels[currentLevelIndex])}
                          label={`Continue to ${applicationLevels[currentLevelIndex]?.name}`}
                          bg={"yellow.400"}
                          color={"gray.800"}
                          width="100%"
                          _hover={{
                            bg: "yellow.500",
                            transform: "translateY(-2px)",
                            boxShadow: "lg"
                          }}
                          size="lg"
                          leftIcon={<ArrowForwardIcon />}
                        />
                      </VStack>
                    )}
                  </VStack>
                </Fade>
              ) : (
                <Fade in={showWelcome} delay={0.3}>
                  <Stack
                    direction={{ base: "column", md: "row" }}
                    align={{ base: "center", md: "none" }}
                    justify={"center"}
                    spacing={4}
                  >
                    <CustomButton
                      as={"a"}
                      link="/login"
                      label="Login to my Account"
                      bg={"yellow.400"}
                      width={{ base: "350px", md: "300px" }}
                      _hover={{
                        bg: "yellow.500",
                        transform: "translateY(-2px)",
                        boxShadow: "lg"
                      }}
                    />
                    <CustomButton
                      as={"a"}
                      link="/register"
                      label="Create New Account"
                      bg={"blue.400"}
                      width={{ base: "350px", md: "300px" }}
                      variant="outline"
                      color={"white"}
                      _hover={{
                        bg: "",
                        color: "yellow.400",
                      }}
                    />
                  </Stack>
                </Fade>
              )}
            </Stack>
          </Box>
        </Box>
      }
    />
  );
}